import { OverlayContainer, useOverlayPosition, useOverlayTrigger } from '@react-aria/overlays'
import { useOverlayTriggerState } from '@react-stately/overlays'
import { mergeProps } from '@react-aria/utils'
import {
  AssigneeType,
  CampaignPropertyValue,
  DefaultType,
  DocumentPropertyValue,
  PersonPropertyValue,
  Property,
  PropertyType,
  PropertyValue as PropertyValueType,
} from 'api/clients/PropertyClient'
import cx from 'classnames'
import { DatePicker as NewDatePicker, DatePickerHandles as NewDatePickerHandles } from 'common/DatePicker/DatePicker'
import { DocPicker, DocPickerHandles } from 'common/DocPicker/DocPicker'
import { OptionSelect, OptionSelectHandles, OptionSelectVariant } from 'common/OptionSelect/OptionSelect'
import { Person as NewPersonPicker, PersonHandles as NewPersonPickerHandles } from 'common/Person/Person'
import { getRandomColor, lightColors } from 'common/PropertyForm/ColorInput'
import { Popover } from 'components/Popover/Popover'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useEvent } from 'react-use'
import { clearPropertyEventName } from 'screens/Finder/components/FileTree/events/clearProperty'
import { EditPropertyEvent, editPropertyEventName } from 'screens/Finder/components/FileTree/events/editProperty'
import {
  SelectPropertyEventDetails,
  selectPropertyEventName,
} from 'screens/Finder/components/FileTree/events/selectProperty'
import { reportError } from 'utils/error'
import { useCurrentWorkspace } from '../../hooks/useCurrentWorkspace'
import Styles from './PropertyValue.module.scss'
import CheckboxEdit from './Types/CheckboxEdit'
import Date from './Types/Date'
import { DateEdit } from './Types/DateEdit'
import Document from './Types/Document'
import DocumentEdit from './Types/DocumentEdit'
import { InlineEditable, InlineEditableHandles } from './Types/InlineEditable/InlineEditable'
import Link from './Types/Link'
import MultiSelect from './Types/MultiSelect'
import Number from './Types/Number'
import Person from './Types/Person'
import PersonEdit from './Types/PersonEdit'
import PersonsList from './Types/PersonsList'
import SingleSelect from './Types/SingleSelect'
import Text from './Types/Text'
import { removeEmptyElements } from './utils'
import { Tag } from 'common/OptionSelect/Tag'
import { DateFormat } from 'common/PropertyForm/Types/Date'
import PostStatus from './Types/PostStatus'
import { Spinner } from 'common/Spinner/Spinner'
import { DateTimePickerEdit } from './Types/DateTimePickerEdit'
import { Button } from 'blaze-ui/Button'
import { ChevronDown } from 'blaze-ui/icons'

export interface Props {
  edit?: boolean
  assignee?: {
    id: string
    type: AssigneeType
  }
  disabled?: boolean
  property: Property
  selectedPropertyValues:
    | PropertyValueType[]
    | DocumentPropertyValue[]
    | PersonPropertyValue[]
    | CampaignPropertyValue[]
  onSelect?: (propertyValue: PropertyValueType[] | DocumentPropertyValue[] | PersonPropertyValue[]) => void
  onCreate?: (propertyValue: PropertyValueType) => void
  onUpdate?: (propertyValue: PropertyValueType) => void
  onDelete?: (propertyValue: PropertyValueType) => void
  className?: string
  valueClassName?: string
  inline?: true
  isEditing: boolean
  setIsEditing?: (isEditing: boolean) => void
  variant?: OptionSelectVariant
}

export const PropertyValue = ({
  property,
  assignee,
  selectedPropertyValues,
  disabled,
  onSelect = () => {},
  onCreate,
  onUpdate,
  onDelete,
  edit = false,
  className,
  valueClassName,
  inline,
  isEditing,
  setIsEditing,
  variant,
}: Props) => {
  const editValueWithoutOpeningPopover = property.type === PropertyType.Checkbox
  const isEntryBasedProperty = [PropertyType.Text, PropertyType.Number, PropertyType.Link].includes(property.type)
  const workspace = useCurrentWorkspace()

  const selectedPropertyValue = selectedPropertyValues[0] as PropertyValueType
  const selectedDocumentValue = selectedPropertyValues[0] as DocumentPropertyValue
  const selectedPersonValue = selectedPropertyValues[0] as PersonPropertyValue

  const inlineEditableRef = useRef<InlineEditableHandles>(null)
  const [propertyValueState, setPropertyValueState] = useState(selectedPropertyValues)
  const handlePropertyValueUpdate = (value: (typeof propertyValueState)[number]) => {
    setPropertyValueState([value])
    onUpdate?.(value)
  }
  const handlePropertyValueSelect = (value: typeof propertyValueState) => {
    setPropertyValueState(value)
    onSelect?.(value)
  }
  const handlePropertyValueCreate = (value: (typeof propertyValueState)[number]) => {
    setPropertyValueState([Object.assign({}, selectedPropertyValues[0], value)])
    onCreate?.(value)
  }
  const handlePropertyValueDelete = (value: (typeof propertyValueState)[number]) => {
    setPropertyValueState([])
    onDelete?.(value)
  }
  useEffect(() => {
    if (
      selectedPropertyValues.length > 0 ||
      property.type === PropertyType.Checkbox ||
      property.type === PropertyType.Document
    ) {
      setPropertyValueState(selectedPropertyValues)
    }
  }, [selectedPropertyValues, property.type])

  const optionSelectRef = useRef<OptionSelectHandles>(null)
  const person2Ref = useRef<NewPersonPickerHandles>(null)
  const newDatePickerRef = useRef<NewDatePickerHandles>(null)
  const docPickerRef = useRef<DocPickerHandles>(null)

  const isDisabled =
    disabled ||
    !property.is_editable ||
    workspace?.guest ||
    editValueWithoutOpeningPopover ||
    selectedPropertyValue?.value_is_disabled
  const [isEdit, setEdit] = useState<boolean>(edit)
  const toggle = () => {
    if (isEdit) {
      // transitioning to false (closing) - select the property
      selectProperty()
    } else {
      clearSelection()
    }
    setEdit(!isEdit)
  }

  const clearSelection = () => {
    document.body.dispatchEvent(
      new CustomEvent<SelectPropertyEventDetails>(selectPropertyEventName, {
        bubbles: false,
        detail: null,
      }),
    )
  }

  const selectProperty = useCallback(() => {
    if (!assignee?.id) return
    document.body.dispatchEvent(
      new CustomEvent<SelectPropertyEventDetails>(selectPropertyEventName, {
        bubbles: false,
        detail: {
          propertyId: property.id,
          assigneeId: assignee.id,
        },
      }),
    )
  }, [property.id, assignee?.id])

  useEvent(
    editPropertyEventName,
    (e) => {
      const evt = e as EditPropertyEvent
      const { anchorFile, anchorProperty } = evt.detail
      if (anchorProperty === 'Name') return
      if (anchorFile.id === assignee?.id && anchorProperty.id === property.id) {
        e.stopImmediatePropagation()
        switch (property.type) {
          case PropertyType.Text:
          case PropertyType.Number:
          case PropertyType.Link:
            setIsEditing?.(true)
            break
          case PropertyType.Checkbox:
            selectedPropertyValues.length
              ? onDelete?.(selectedPropertyValues[0])
              : onCreate?.({
                  value: '',
                  property_id: property.id,
                } as PropertyValueType)
            break
          case PropertyType.Document:
            if (property.is_editable) {
              docPickerRef.current?.setEditing(true)
            }
            break
          case PropertyType.Date:
            if (property.is_editable) {
              newDatePickerRef.current?.setEditing(true)
            }
            break
          case PropertyType.Person:
            if (property.name !== 'Owner') {
              person2Ref.current?.setEditing(true)
            }
            break
          case PropertyType.SingleSelect:
          case PropertyType.MultiSelect:
            optionSelectRef.current?.setEditing(true)
            break
          default:
            if (process.env.NODE_ENV === 'production') {
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              reportError(`Unknown property type ${property!.type}`)
            } else {
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              throw new Error(`Unknown property type ${property!.type}`)
            }
            break
        }
      }
    },
    document.body,
  )

  useEvent(
    clearPropertyEventName,
    (e) => {
      const evt = e as EditPropertyEvent
      const { anchorFile, anchorProperty } = evt.detail
      if (anchorProperty === 'Name') return
      if (anchorFile.id === assignee?.id && anchorProperty.id === property.id) {
        e.stopImmediatePropagation()
        switch (property.type) {
          case PropertyType.Text:
          case PropertyType.Number:
          case PropertyType.Link:
            inlineEditableRef.current?.clear()
        }
      }
    },
    document.body,
  )

  useEffect(() => {
    setEdit(edit)
  }, [edit])

  const renderValue = (
    property_type: string,
    selectedPropertyValues: PropertyValueType[] | DocumentPropertyValue[] | PersonPropertyValue[],
  ) => {
    if (!selectedPropertyValues.length && !editValueWithoutOpeningPopover) {
      if (property_type === PropertyType.Date && property.name === 'Post Date & Time') {
        return 'Set date'
      }
      return null
    }

    switch (property_type) {
      case PropertyType.Text:
        return <Text propertyValue={propertyValueState[0]} />
      case PropertyType.Number:
        return (
          <Number propertyValue={propertyValueState[0]} format={property.meta?.number_format} disabled={isDisabled} />
        )
      case PropertyType.Date:
        if (property.meta?.date_format === DateFormat.DateTime) {
          return (
            <Date
              propertyValue={propertyValueState[0]}
              dateFormat={property.meta?.date_format}
              placeholder="Set date"
            />
          )
        }
        return <Date propertyValue={propertyValueState[0]} dateFormat={property.meta?.date_format} />
      case PropertyType.Person:
        return property.meta?.allow_mentioning_multiple_people ? (
          <PersonsList
            propertyValues={selectedPropertyValues as PersonPropertyValue[]}
            onSelect={(...args) => {
              onSelect(...args)
              toggle()
            }}
          />
        ) : (
          <Person propertyValue={selectedPersonValue} />
        )
      case PropertyType.SingleSelect:
        return <SingleSelect propertyValue={selectedPropertyValue} />
      case PropertyType.MultiSelect:
        return <MultiSelect inline={inline} selectedPropertyValues={selectedPropertyValues} />
      case PropertyType.Document:
        return <Document propertyValue={selectedDocumentValue} />
      case PropertyType.Link:
        return <Link propertyValue={propertyValueState[0]} />
      case PropertyType.Checkbox:
        return (
          <CheckboxEdit
            property={property}
            propertyValue={propertyValueState[0]}
            onCreate={handlePropertyValueCreate}
            onDelete={handlePropertyValueDelete}
          />
        )
    }
  }

  const renderDropdown = (
    property_type: string,
    selectedPropertyValues: PropertyValueType[] | PersonPropertyValue[],
  ) => {
    switch (property_type) {
      case PropertyType.Person:
        return (
          <PersonEdit
            property={property}
            selectedPropertyValues={selectedPropertyValues}
            assignee={assignee}
            onSelect={(...args) => {
              onSelect(...args)
              toggle()
            }}
            onCancel={() => {
              toggle()
            }}
          />
        )
      case PropertyType.Document:
        return (
          <DocumentEdit
            assignee={assignee}
            onSelect={(...args) => {
              onSelect(...args)
              toggle()
            }}
            onCancel={toggle}
          />
        )
      case PropertyType.Date:
        if (property.meta?.date_format === DateFormat.DateTime && assignee) {
          return (
            <DateTimePickerEdit
              assignee={assignee}
              selectedPropertyValues={selectedPropertyValues}
              onClose={() => {
                toggle()
              }}
              onSelect={(date: string) => {
                if (selectedPropertyValues[0]?.value) {
                  handlePropertyValueUpdate({
                    ...selectedPropertyValues[0],
                    value: date,
                  })
                } else {
                  handlePropertyValueCreate?.({
                    value: date,
                    property_id: property.id,
                  } as PropertyValueType)
                }
                toggle()
              }}
            />
          )
        }
        return (
          <DateEdit
            property={property}
            propertyValue={propertyValueState[0]}
            onCreate={(...args) => {
              handlePropertyValueCreate(...args)
              toggle()
            }}
            onUpdate={(...args) => {
              handlePropertyValueUpdate(...args)
              toggle()
            }}
            onCancel={toggle}
          />
        )
    }
  }

  /*
   * Dropdown
   */
  const triggerRef = useRef<HTMLButtonElement>(null)
  const overlayRef = useRef(null)

  const overlayState = useOverlayTriggerState({ isOpen: isEdit && !editValueWithoutOpeningPopover })
  const { triggerProps, overlayProps } = useOverlayTrigger({ type: 'dialog' }, overlayState, triggerRef)
  const { overlayProps: positionProps, updatePosition } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef,
    placement: 'bottom left',
    offset: 4,
    isOpen: overlayState.isOpen,
    boundaryElement: document.getElementById('workspace-dashboard') || document.body,
  })

  useEffect(updatePosition, [triggerRef.current?.className, updatePosition])

  const isStatusProperty = property.default_type === DefaultType.Status

  const onEditingStateChangeHandler = useCallback(
    (isEditing: boolean) => {
      if (property.type === PropertyType.Date) {
        // Date property is an exception, we change the selection on any transition to/from editing state
        selectProperty()
        return
      }
      if (isEditing) {
        clearSelection()
      } else {
        selectProperty()
      }
    },
    [selectProperty, property.type],
  )

  const renderPopoverContent = () => {
    if (property.type === PropertyType.MultiSelect) {
      return (
        <div className={Styles.optionSelectWrapper}>
          <OptionSelect
            ref={optionSelectRef}
            mode="multiple"
            variant={variant}
            isEditable={!isDisabled}
            onChange={(newSelection) => {
              // Preserve the order the user selected. Do not use the order that comes in the property object
              const selectedPropertyValues = removeEmptyElements(
                newSelection.map((s) => property.property_values.find((pv) => pv.value === s)),
              )
              onSelect(selectedPropertyValues)
            }}
            onEditingStateChange={onEditingStateChangeHandler}
            onCreated={(newValue, color?: string) => {
              onCreate?.({
                value: newValue,
                property_id: property.id,
                position: property.property_values.length,
                meta: {
                  color: color || getRandomColor(),
                },
              } as PropertyValueType)
            }}
            onUpdated={onUpdate}
            onDeleted={onDelete}
            selected={selectedPropertyValues.flatMap((pv) => (!!pv.value ? [pv.value] : []))}
            availableOptions={property.property_values}
          />
        </div>
      )
    }
    if (property.type === PropertyType.SingleSelect) {
      if (property.default_type === DefaultType.PostStatus) {
        if (!assignee) {
          return <Spinner />
        }
        return (
          <PostStatus
            propertyValue={selectedPropertyValue}
            assignee={assignee}
            onSelect={(newSelection) => onSelect(property.property_values.filter((pv) => pv.value === newSelection))}
          />
        )
      }
      return (
        <div className={Styles.optionSelectWrapper}>
          <OptionSelect
            variant={variant}
            ref={optionSelectRef}
            mode="single"
            isEditable={!isDisabled}
            onChange={(newSelection) => {
              onSelect(property.property_values.filter((pv) => pv.value === newSelection))
            }}
            onEditingStateChange={onEditingStateChangeHandler}
            onCreated={(newValue, color?: string) => {
              onCreate?.({
                value: newValue,
                property_id: property.id,
                position: property.property_values.length,
                meta: {
                  color: color || getRandomColor(),
                },
              } as PropertyValueType)
            }}
            onUpdated={onUpdate}
            onDeleted={onDelete}
            selected={selectedPropertyValues.find((pv) => !!pv.value)?.value}
            availableOptions={property.property_values}
            canEditOptions={!isStatusProperty}
            allowEmpty={!isStatusProperty}
          />
        </div>
      )
    }
    if (property.type === PropertyType.Campaign) {
      const campaignValue = selectedPropertyValues[0] as CampaignPropertyValue
      const campaignName = campaignValue?.value.name

      if (!campaignName) return null
      const color = lightColors[campaignValue.id % lightColors.length]
      return (
        <div className={Styles.campaignValue}>
          <Tag color={color}>{campaignName}</Tag>
        </div>
      )
    }
    if (property.type === PropertyType.Person) {
      return (
        <NewPersonPicker
          wrapperClassName={Styles.personPickerWrapper}
          allowMultiple={property.meta?.allow_mentioning_multiple_people}
          ref={person2Ref}
          variant={variant === OptionSelectVariant.Filter ? 'filter' : 'table-person-picker'}
          value={(selectedPropertyValues as PersonPropertyValue[]).map((ppv) => {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return property.name === 'Owner' ? ppv.value.creator_id! : ppv.id
          })}
          onUpdate={(idList) => {
            if (!Array.isArray(idList)) return

            onSelect(idList.map((id) => ({ id } as PersonPropertyValue)))
          }}
          onClick={property.is_editable ? undefined : selectProperty}
          readOnly={!property.is_editable}
          onEditingStateChange={onEditingStateChangeHandler}
          disabled={isDisabled}
        />
      )
    }
    if (
      property.type === PropertyType.Date &&
      property.is_editable &&
      property.meta?.date_format !== DateFormat.DateTime
    ) {
      return (
        <NewDatePicker
          wrapperClassName={Styles.dateWrapper}
          ref={newDatePickerRef}
          variant={variant === OptionSelectVariant.Filter ? 'filter' : 'table-date-picker'}
          size="md"
          value={selectedPropertyValues[0]?.value ?? ''}
          readOnly={!property.is_editable}
          onChange={(date) => {
            if (selectedPropertyValues[0]?.value) {
              handlePropertyValueUpdate({
                ...selectedPropertyValues[0],
                value: date,
              })
            } else {
              handlePropertyValueCreate?.({
                value: date,
                property_id: property.id,
              } as PropertyValueType)
            }
          }}
          onDeleted={() => {
            handlePropertyValueDelete(selectedPropertyValues[0])
          }}
          onClick={property.is_editable ? undefined : selectProperty}
          onEditingStateChange={onEditingStateChangeHandler}
          dateFormat={property.meta?.date_format}
        />
      )
    }

    if (property.type === PropertyType.Document) {
      return (
        <DocPicker
          ref={docPickerRef}
          isEditable={!isDisabled}
          onChange={(newDoc) => {
            const docPV: DocumentPropertyValue = {
              value: {
                header_emoji: newDoc.header_emoji ?? '',
                title: newDoc.title,
                key: newDoc.key,
              },
              id: newDoc.key,
              property_id: property.id,
            }
            handlePropertyValueSelect([docPV])
          }}
          onEditingStateChange={onEditingStateChangeHandler}
          onDelete={() => handlePropertyValueSelect([])}
          selected={propertyValueState[0] as DocumentPropertyValue}
        />
      )
    }

    return (
      <>
        <Button
          className={Styles.propertyButton}
          {...mergeProps(triggerProps, {
            ref: triggerRef,
            isDisabled,
            onPress: () => {
              if (isDisabled) {
                selectProperty()
              } else {
                toggle()
              }
            },
          })}
          variant="tertiary"
          size="md"
          fullWidth
          endIcon={ChevronDown}
        >
          <span className={cx(Styles.propertyValue, { [Styles.inline]: inline }, valueClassName)}>
            {renderValue(property.type, propertyValueState)}
          </span>
        </Button>
        {overlayState.isOpen && (
          <OverlayContainer>
            <Popover
              {...overlayProps}
              {...positionProps}
              isOpen={overlayState.isOpen}
              onClose={toggle}
              ref={overlayRef}
              isDismissable
              className={Styles.popoverPropertyValue}
            >
              {renderDropdown(property.type, selectedPropertyValues)}
            </Popover>
          </OverlayContainer>
        )}
      </>
    )
  }

  if (editValueWithoutOpeningPopover) {
    return (
      <div className={cx(Styles.valueContainer, className)}>
        <button className={Styles.value}>{renderValue(property.type, selectedPropertyValues)}</button>
      </div>
    )
  }

  if (isEntryBasedProperty || (property.type === PropertyType.Date && !property.is_editable)) {
    return (
      <div
        className={cx(Styles.valueContainer, className, Styles.isEntryBasedProperty, {
          [Styles.paddingLeft]: !isEditing,
          [Styles.focused]: isEditing,
        })}
      >
        <InlineEditable
          ref={inlineEditableRef}
          property={property}
          // with realtime, we need to pass in an empty string otherwise we get an error about uncontrolled input changing to be controlled
          propertyValue={propertyValueState[0] || ''}
          onCreate={handlePropertyValueCreate}
          onUpdate={handlePropertyValueUpdate}
          assigneeId={assignee?.id}
          isEditing={isEditing}
          setIsEditing={(isEditing) => {
            selectProperty()
            setIsEditing?.(isEditing)
          }}
          disabled={!!isDisabled}
          isBlank={!!!propertyValueState[0]?.value}
          valueClassName={valueClassName}
        >
          {renderValue(property.type, propertyValueState)}
        </InlineEditable>
      </div>
    )
  }

  return renderPopoverContent()
}
